import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Pencil, Trash2, Plus, Calendar } from 'lucide-react';
import { format, differenceInDays } from 'date-fns';
import type { Goal } from '@/lib/types';
import { useCurrency } from '@/hooks/useCurrency';

interface GoalCardProps {
  goal: Goal;
  onEdit: (goal: Goal) => void;
  onDelete: (id: string) => void;
  onContribute: (goal: Goal) => void;
}

const statusColors: Record<string, string> = {
  active: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  paused: 'bg-yellow-100 text-yellow-800',
};

const typeLabels: Record<string, string> = {
  savings: 'Savings',
  debt_payoff: 'Debt Payoff',
  investment: 'Investment',
  other: 'Other',
};

export default function GoalCard({ goal, onEdit, onDelete, onContribute }: GoalCardProps) {
  const { fmt } = useCurrency();
  const percentage =
    goal.targetAmount > 0
      ? Math.round((goal.currentAmount / goal.targetAmount) * 100)
      : 0;
  const remaining = goal.targetAmount - goal.currentAmount;
  const daysLeft = goal.targetDate
    ? differenceInDays(new Date(goal.targetDate), new Date())
    : null;

  return (
    <Card className="transition-all duration-200 hover:shadow-md">
      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-4">
        <div className="flex items-center gap-3">
          <div
            className="p-2 rounded-full flex items-center justify-center"
            style={{ backgroundColor: goal.color ? `${goal.color}15` : 'hsl(var(--primary) / 0.1)' }}
          >
            <div
              className="h-4 w-4 flex items-center justify-center font-bold text-xs"
              style={{ color: goal.color || 'hsl(var(--primary))' }}
            >
              {goal.emoji || goal.name.charAt(0).toUpperCase()}
            </div>
          </div>
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <CardTitle className="text-base font-semibold">{goal.name}</CardTitle>
              <Badge variant="outline" className={statusColors[goal.status] || ''}>
                {goal.status}
              </Badge>
            </div>
            <CardDescription>{typeLabels[goal.type] || goal.type}</CardDescription>
          </div>
        </div>
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" className="h-10 w-10 sm:h-8 sm:w-8 text-muted-foreground hover:text-primary" onClick={() => onEdit(goal)} aria-label="Edit goal">
            <Pencil className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-10 w-10 sm:h-8 sm:w-8 text-muted-foreground hover:text-destructive" onClick={() => onDelete(goal.id)} aria-label="Delete goal">
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Big percentage */}
        <div className="flex items-center justify-between">
          <span className="text-2xl sm:text-3xl font-bold text-primary">{percentage}%</span>
          {goal.status === 'active' && (
            <Button size="sm" onClick={() => onContribute(goal)}>
              <Plus className="mr-1 h-4 w-4" />
              Contribute
            </Button>
          )}
        </div>

        {/* Progress bar */}
        <Progress value={Math.min(percentage, 100)} className="h-3" />

        {/* Amounts */}
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            Current: {fmt(goal.currentAmount)}
          </span>
          <span className="font-medium">
            Target: {fmt(goal.targetAmount)}
          </span>
        </div>

        {/* Remaining and deadline */}
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>
            {remaining > 0
              ? `${fmt(remaining)} to go`
              : 'Target reached!'}
          </span>
          {goal.targetDate && (
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {daysLeft !== null && daysLeft > 0
                ? `${daysLeft} days left`
                : daysLeft === 0
                  ? 'Due today'
                  : 'Overdue'}
              {' · '}
              {format(new Date(goal.targetDate), 'MMM d, yyyy')}
            </span>
          )}
        </div>

        {/* Milestones preview */}
        {goal.milestones && goal.milestones.length > 0 && (
          <div className="flex gap-1 pt-1">
            {goal.milestones.map((m, i) => (
              <div
                key={i}
                className={`h-2 w-2 rounded-full ${m.achieved ? 'bg-green-500' : 'bg-muted'}`}
                title={`${fmt(m.amount)} milestone${m.achieved ? ' (achieved)' : ''}`}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
